// Motor de tarifas CREG — PORTADO de FluxAI (api/ Laravel: MotorTarifas + CalculadoraFactura
// + TarifarioCregSeeder) al stack de Solar Hub. Misma fórmula y datos de referencia, en JS puro.
//
// Reemplaza la tarifa "promedio ±30%" del cotizador por el Costo Unitario (CU) real por
// NIVEL DE TENSIÓN y los cargos por ESTRATO. CU = G+T+D+Cv+Pr+R ($/kWh).
//
// Datos de REFERENCIA (operador genérico, orden de magnitud Colombia 2024–2026, SIN).
// NO son tarifas oficiales: el operador de red real sobreescribe con su pliego (CREG/SUI).
// Espejo de FluxAI TarifarioCregSeeder.

// CU de referencia por nivel de tensión ($/kWh): [G, T, D, Cv, Pr, R]
export const CU_POR_NIVEL = {
  1: { generacion: 380, transmision: 55, distribucion: 260, comercializacion: 90, perdidas: 95, restricciones: 20 },
  2: { generacion: 380, transmision: 55, distribucion: 200, comercializacion: 80, perdidas: 85, restricciones: 20 },
  3: { generacion: 380, transmision: 55, distribucion: 140, comercializacion: 70, perdidas: 75, restricciones: 18 },
  4: { generacion: 380, transmision: 55, distribucion: 90,  comercializacion: 60, perdidas: 65, restricciones: 15 },
};

// Subsidio (estrato < 4) y contribución (estrato > 4) — Ley 142/1994, 1117/2006.
export const CARGO_POR_ESTRATO = {
  1: { subsidio_pct: 60, contribucion_pct: 0 },
  2: { subsidio_pct: 50, contribucion_pct: 0 },
  3: { subsidio_pct: 15, contribucion_pct: 0 },
  4: { subsidio_pct: 0,  contribucion_pct: 0 },
  5: { subsidio_pct: 0,  contribucion_pct: 20 },
  6: { subsidio_pct: 0,  contribucion_pct: 20 },
};

export const USO = { RESIDENCIAL: 'residencial', COMERCIAL: 'comercial', INDUSTRIAL: 'industrial', OFICIAL: 'oficial' };

/** Componentes del CU para un nivel (cae a N1 si el nivel es inválido). */
export function componentesCU(nivel = 1) {
  return CU_POR_NIVEL[Number(nivel)] || CU_POR_NIVEL[1];
}

/** Costo Unitario total $/kWh para un nivel de tensión (G+T+D+Cv+Pr+R). */
export function costoUnitario(nivel = 1) {
  const c = componentesCU(nivel);
  return c.generacion + c.transmision + c.distribucion + c.comercializacion + c.perdidas + c.restricciones;
}

/** Cargos regulatorios por estrato (subsidio/contribución). */
export function cargoEstrato(estrato = 0) {
  return CARGO_POR_ESTRATO[Number(estrato)] || { subsidio_pct: 0, contribucion_pct: 0 };
}

/**
 * Resuelve el pliego de referencia para un cliente. Si `operadorTarifas` trae el pliego
 * real del operador (mismo shape de componentes), se usa; si no, cae a la referencia por nivel.
 * Espejo de MotorTarifas.pliegoPara (resolución por operador → respaldo genérico).
 */
export function resolverPliego({ nivel = 1, operadorPliego = null } = {}) {
  const base = operadorPliego && operadorPliego.generacion != null ? operadorPliego : componentesCU(nivel);
  const costo_unitario = (base.generacion || 0) + (base.transmision || 0) + (base.distribucion || 0) +
    (base.comercializacion || 0) + (base.perdidas || 0) + (base.restricciones || 0);
  return { ...base, costo_unitario, cargo_fijo: base.cargo_fijo || 0, fuente: operadorPliego ? 'operador' : 'referencia' };
}

/**
 * Calcula la factura de energía CREG. Espejo FIEL de FluxAI CalculadoraFactura.calcular:
 *   energía = consumo × CU
 *   subsidio (estrato<4) = −min(consumo, subsistencia) × CU × subsidio%
 *   contribución (estrato>4 && contribuye) = energía × contribucion%
 *   alumbrado (si aplica) = monto fijo | energía × %
 *   reactiva (COM/IND) = D × (kVARh_cap + kVARh_ind)
 *   IVA: residencial EXCLUIDO; no residencial 19% sobre (energía + reactiva + cargo fijo)
 */
export function calcularFacturaCREG({
  nivel = 1, estrato = 0, clase_uso = USO.RESIDENCIAL, contribuye = false,
  aplica_alumbrado = false, consumo_subsistencia_kwh = 0,
  operadorPliego = null, alumbrado = null, // {tipo:'monto'|'pct', valor:number}
  consumoKwh = 0, reactivaCapacitivaKvarh = 0, reactivaInductivaKvarh = 0, ivaPct = 19,
  contribucion_pct = null, // override explícito (ver nota abajo); null = derivar del estrato
} = {}) {
  const pliego = resolverPliego({ nivel, operadorPliego });
  const precioKwh = pliego.costo_unitario;
  const energia = consumoKwh * precioKwh;
  const e = Number(estrato) || 0;
  const cargo = cargoEstrato(e);

  let subsidio = 0;
  if (e > 0 && e < 4 && cargo.subsidio_pct > 0) {
    const base = consumo_subsistencia_kwh > 0 ? Math.min(consumoKwh, consumo_subsistencia_kwh) : consumoKwh;
    subsidio = -1 * (base * precioKwh * (cargo.subsidio_pct / 100));
  }

  // La contribución de solidaridad (Ley 142/1994 art. 89) la determina el ESTRATO para el
  // usuario residencial, pero también la paga el COMERCIAL (20%) — que no tiene estrato, así
  // que la regla `estrato > 4` nunca lo alcanzaba. `contribucion_pct` permite fijarla de forma
  // explícita; sin él, el comportamiento es EXACTAMENTE el de antes (derivado del estrato).
  let contribucion = 0;
  const pctContrib = contribucion_pct != null
    ? (Number(contribucion_pct) || 0)
    : (e > 4 ? cargo.contribucion_pct : 0);
  if (contribuye && pctContrib > 0) {
    contribucion = energia * (pctContrib / 100);
  }

  let cargoAlumbrado = 0;
  if (aplica_alumbrado && alumbrado) {
    cargoAlumbrado = alumbrado.tipo === 'monto' ? (alumbrado.valor || 0) : energia * ((alumbrado.valor || 0) / 100);
  }

  let reactiva = 0;
  const esComInd = clase_uso === USO.COMERCIAL || clase_uso === USO.INDUSTRIAL;
  if (esComInd) {
    reactiva = (pliego.distribucion || 0) * (reactivaCapacitivaKvarh + reactivaInductivaKvarh);
  }

  const cargoFijo = pliego.cargo_fijo || 0;
  const subtotal = energia + contribucion + subsidio + cargoAlumbrado + reactiva + cargoFijo;

  const gravado = [USO.COMERCIAL, USO.INDUSTRIAL, USO.OFICIAL].includes(clase_uso);
  const baseIva = gravado ? (energia + reactiva + cargoFijo) : 0;
  const iva = baseIva * (ivaPct / 100);
  const total = subtotal + iva;

  return {
    precio_kwh: round(precioKwh, 4),
    componentes: {
      generacion: pliego.generacion, transmision: pliego.transmision, distribucion: pliego.distribucion,
      comercializacion: pliego.comercializacion, perdidas: pliego.perdidas, restricciones: pliego.restricciones,
    },
    fuente_pliego: pliego.fuente,
    energia_activa: round(energia, 2),
    subsidio: round(subsidio, 2),
    contribucion: round(contribucion, 2),
    alumbrado_publico: round(cargoAlumbrado, 2),
    reactiva: round(reactiva, 2),
    cargo_fijo: round(cargoFijo, 2),
    subtotal: round(subtotal, 2),
    iva_pct: gravado ? ivaPct : 0,
    iva: round(iva, 2),
    total: round(total, 2),
  };
}

function round(x, d) { const p = Math.pow(10, d); return Math.round((x + Number.EPSILON) * p) / p; }

// ============================================================================
// RESOLVER DE TARIFA DE LA SUITE — una sola respuesta a "¿cuánto vale el kWh?"
// ============================================================================
// Antes cada app respondía distinto: el cotizador multiplicaba un promedio plano del
// operador por un factor de estrato, este motor vivía sin usarse, y el workflow viejo
// llevaba una tercera copia de la tabla. Esta función es el único punto de entrada.

/**
 * Consumo de subsistencia — el subsidio SOLO cubre hasta acá; el excedente se paga pleno.
 * Res. CREG 355/2004 y Res. MME 180466/2005: 173 kWh/mes por debajo de 1.000 msnm,
 * 130 kWh/mes a 1.000 msnm o más (menor demanda de bombeo/calentamiento en altura).
 */
export const SUBSISTENCIA_KWH = { bajo: 173, alto: 130 };

/**
 * Traduce el selector del cotizador (E1…E6 / COM / IND2 / IND3) al modelo CREG.
 * `contribucion_pct`: residencial 5 y 6 aportan 20%; COMERCIAL también 20%; el
 * INDUSTRIAL quedó EXENTO desde 2012 (Ley 1430/2010 art. 2, desmonte de la contribución
 * industrial) — por eso IND2/IND3 van en 0 y no en 20.
 */
export const PERFIL_TARIFARIO = {
  E1:   { nivel: 1, estrato: 1, uso: USO.RESIDENCIAL, contribucion_pct: 0 },
  E2:   { nivel: 1, estrato: 2, uso: USO.RESIDENCIAL, contribucion_pct: 0 },
  E3:   { nivel: 1, estrato: 3, uso: USO.RESIDENCIAL, contribucion_pct: 0 },
  E4:   { nivel: 1, estrato: 4, uso: USO.RESIDENCIAL, contribucion_pct: 0 },
  E5:   { nivel: 1, estrato: 5, uso: USO.RESIDENCIAL, contribucion_pct: 20 },
  E6:   { nivel: 1, estrato: 6, uso: USO.RESIDENCIAL, contribucion_pct: 20 },
  COM:  { nivel: 1, estrato: 0, uso: USO.COMERCIAL,   contribucion_pct: 20 },
  IND2: { nivel: 2, estrato: 0, uso: USO.INDUSTRIAL,  contribucion_pct: 0 },
  IND3: { nivel: 3, estrato: 0, uso: USO.INDUSTRIAL,  contribucion_pct: 0 },
};

/**
 * Reparte un CU total conocido entre los seis componentes CREG, conservando la proporción
 * de la referencia del nivel. Sirve para NO perder el CU real del operador cuando solo
 * tenemos su tarifa plana: la magnitud es la del operador, la estructura la de la norma.
 */
export function escalarComponentes(cuObjetivo, nivel = 1) {
  const ref = componentesCU(nivel);
  const total = costoUnitario(nivel);
  if (!(cuObjetivo > 0) || !(total > 0)) return { ...ref };
  const k = cuObjetivo / total;
  return {
    generacion: ref.generacion * k, transmision: ref.transmision * k,
    distribucion: ref.distribucion * k, comercializacion: ref.comercializacion * k,
    perdidas: ref.perdidas * k, restricciones: ref.restricciones * k,
  };
}


/**
 * LA TARIFA PUBLICADA POR EL OPERADOR — cuando existe, le gana al modelo.
 *
 * 🔴 Medido el 2026-09-07 con CEELVA (Mitú, ZNI), 300 kWh: el modelo CREG daba 722.366 COP para un
 * estrato 4 y el pliego publicado dice **279.095**. En una Zona No Interconectada la tarifa al
 * usuario no es «CU menos el subsidio CREG» —el Estado cubre la diferencia (FAZNI/FOES) y el OR
 * publica lo que va a cobrar—, así que aplicar el modelo del interconectado infla el recibo 2,6
 * veces. Y el ahorro solar se calcula CONTRA ese recibo.
 *
 * 🔑 VIVE EN EL MOTOR, no en el backend, porque lo usan los DOS: la ruta `tarifa-creg` y el
 * cotizador, que resuelve la tarifa en el navegador. Dos implementaciones del mismo cobro es
 * exactamente cómo diverge una cifra que el cliente ve en pantalla y otra que sale en la propuesta.
 */
export function cobroPublicado(tarifa, { estrato, consumoKwh } = {}) {
  if (!tarifa || !tarifa.estratos) return null;
  const e = Number(estrato);
  const q = Number(consumoKwh);
  const fila = tarifa.estratos[e];
  if (!fila || !Number.isFinite(q) || q < 0) return null;

  const cs = Math.min(q, tarifa.cs_kwh);
  const medio = Math.max(0, Math.min(q, 800) - tarifa.cs_kwh);
  const alto = Math.max(0, q - 800);
  const total = cs * fila[0] + medio * fila[1] + alto * fila[2];
  return {
    total: Math.round(total),
    precio_kwh: q > 0 ? Number((total / q).toFixed(4)) : 0,
    // 🔑 El MARGINAL es el precio del tramo donde cae el kWh SIGUIENTE, que es el que desplaza cada
    // kWh solar. En CEELVA, un usuario de más de 800 kWh desplaza kWh de 2.666 COP: por eso allá el
    // payback es corto de verdad, y por eso confundir marginal con promedio cambia el negocio.
    marginal: q < tarifa.cs_kwh ? fila[0] : (q < 800 ? fila[1] : fila[2]),
    tramos: {
      subsistencia: { kwh: cs, precio: fila[0] },
      intermedio: { kwh: medio, precio: fila[1] },
      pleno: { kwh: alto, precio: fila[2] },
    },
    supuestos: {
      propiedad: tarifa.propiedad,
      cs_kwh: tarifa.cs_kwh,
      vigencia: tarifa.vigencia,
      subsidio: 'ya incluido en la tarifa publicada (ZNI): no se le resta nada encima',
    },
  };
}

/** Precio publicado de un sector no residencial (comercial · industrial · oficial), o `null`. */
export function precioNoResidencial(tabla, claseUso) {
  if (!tabla) return null;
  const v = tabla[String(claseUso || '').toLowerCase().trim()];
  return Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Resuelve la tarifa de un usuario. Devuelve DOS precios, que no son el mismo número:
 *
 *  · `promedio` — lo que paga hoy por kWh (subsidio/contribución/IVA repartidos sobre
 *    todo el consumo). Es el número honesto para mostrarle "tu tarifa actual".
 *  · `marginal` — lo que cuesta el kWh SIGUIENTE, y por lo tanto lo que AHORRA cada kWh
 *    solar. Es el que debe entrar al ROI.
 *
 * La distinción no es cosmética: para un hogar subsidiado que consume por encima de la
 * subsistencia, los kWh que desplaza el sol son los NO subsidiados → se ahorran al CU
 * pleno, no al subsidiado. Usar el promedio ahí subestima el ahorro hasta 2×.
 *
 * Precedencia de la fuente, de más real a menos: componentes explícitos del operador →
 * CU real del operador (repartido) → referencia CREG del nivel. `fuente` lo declara para
 * que la UI nunca presente una referencia como si fuera el pliego del operador.
 *
 * @param {number|null} cuBase CU total del operador en NIVEL 1 ($/kWh).
 * @param {object|null} componentes Pliego real del operador {generacion,…,restricciones}.
 */
export function resolverTarifa({
  cuBase = null, componentes = null, estrato = 'E4', consumoKwh = 0,
  subsistenciaKwh = null, altitudMsnm = null,
  // Las tablas que publica el operador (hoy CEELVA). Cuando existen, le ganan al modelo.
  tarifaPublicada = null, tarifaNoResidencial = null,
} = {}) {
  const perfil = PERFIL_TARIFARIO[estrato] || PERFIL_TARIFARIO.E4;
  const esResidencial = perfil.uso === USO.RESIDENCIAL;

  // El CU que publica el operador es de nivel 1. Para N2/N3 se traslada con la razón entre
  // niveles de la referencia CREG: conserva la magnitud REAL del operador y refleja que en
  // media tensión se paga menos distribución.
  const base = Number(cuBase) > 0
    ? Number(cuBase) * (costoUnitario(perfil.nivel) / costoUnitario(1))
    : 0;

  let pliego, fuente;
  if (componentes && componentes.generacion != null) { pliego = componentes; fuente = 'pliego'; }
  else if (base > 0) { pliego = escalarComponentes(base, perfil.nivel); fuente = 'operador'; }
  else { pliego = componentesCU(perfil.nivel); fuente = 'referencia'; }

  const subsistencia = !esResidencial ? 0
    : (subsistenciaKwh != null ? Number(subsistenciaKwh)
      : (altitudMsnm != null && Number(altitudMsnm) >= 1000 ? SUBSISTENCIA_KWH.alto : SUBSISTENCIA_KWH.bajo));

  const consumo = Math.max(0, Number(consumoKwh) || 0);
  const factura = calcularFacturaCREG({
    nivel: perfil.nivel, estrato: perfil.estrato, clase_uso: perfil.uso,
    contribuye: perfil.contribucion_pct > 0, contribucion_pct: perfil.contribucion_pct,
    consumo_subsistencia_kwh: subsistencia, operadorPliego: pliego, consumoKwh: consumo,
  });

  const cu = factura.precio_kwh;
  const cargo = cargoEstrato(perfil.estrato);
  const fracIva = esResidencial ? 0 : (factura.iva_pct / 100);   // residencial: energía excluida de IVA
  const fracContrib = perfil.contribucion_pct / 100;
  // Dentro de la subsistencia el kWh siguiente SÍ va subsidiado; por encima, no.
  const dentroSubsidio = esResidencial && subsistencia > 0 && consumo > 0 && consumo < subsistencia;
  const fracSubsidio = dentroSubsidio ? (cargo.subsidio_pct / 100) : 0;

  // Derivada del total respecto al consumo, en la MISMA forma que arma la factura el motor
  // (la contribución grava la energía; el IVA grava energía, no la contribución).
  const marginal = cu * (1 - fracSubsidio + fracContrib + fracIva);
  const promedio = consumo > 0 ? factura.total / consumo : marginal;

  // 🔴 SI EL OPERADOR PUBLICA SU TARIFA, ESA ES LA QUE PAGA EL CLIENTE — y por lo tanto la que
  // desplaza el kWh solar. Ver `cobroPublicado`: con el modelo CREG sobre una ZNI el recibo salía
  // 2,6 veces más alto, y el ahorro se calcula contra el recibo.
  const publicado = esResidencial
    ? cobroPublicado(tarifaPublicada, { estrato: perfil.estrato, consumoKwh: consumo })
    : null;
  const precioSector = esResidencial ? null : precioNoResidencial(tarifaNoResidencial, perfil.uso);
  if (publicado || precioSector) {
    const marg = publicado ? publicado.marginal : precioSector;
    const prom = publicado ? publicado.precio_kwh : precioSector;
    return {
      cu: Math.round(cu),
      marginal: Math.round(marg),
      promedio: Math.round(prom),
      fuente: 'tarifa_publicada',
      perfil,
      componentes: factura.componentes,
      subsistenciaKwh: subsistencia,
      dentroSubsidio: false,      // el subsidio ya viene dentro de la tarifa publicada
      subsidioPct: 0,
      publicado: publicado || { precio_kwh: precioSector, marginal: precioSector },
    };
  }

  return {
    cu: Math.round(cu),
    marginal: Math.round(marginal),
    promedio: Math.round(promedio),
    fuente,                       // 'pliego' | 'operador' | 'referencia' | 'tarifa_publicada'
    perfil, componentes: factura.componentes,
    subsistenciaKwh: subsistencia, dentroSubsidio,
    subsidioPct: dentroSubsidio ? cargo.subsidio_pct : 0,
    subsidioTopePct: cargo.subsidio_pct,
    contribucionPct: perfil.contribucion_pct,
    ivaPct: Math.round(fracIva * 100),
    factura,
  };
}
